import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import ApproverTracker from "../components/ApproverTracker";
import { getEmployeeDetailsForStaffQuarter, submitStaffQuarterRegistration, getStaffQuarterPools, getPreferredLocations, getStaffQuarterQuarterTypes } from "../../../../core/data/redux/staffQuarterSlice";
import { getMyProfile } from "../../../../core/data/redux/employeesSlice";

const NewRegistration: React.FC = () => {
  const routes = all_routes as any;
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
  const employeesState = useSelector((state: any) => state.employeesState);
  const apiData = staffQuarterState?.employeeDetails;
  const employeeDetails = apiData || {};
  const poolList = staffQuarterState?.pools?.data || [];
  const [locations, setLocations] = useState<any[]>([]);
  const [quarterTypes, setQuarterTypes] = useState<any[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const eligibility = {
    eeGroup: apiData?.employeeGroup,
    id: apiData?.quarterTypeEligibility?.id || null,
    eligibilityForQuarterType: apiData?.quarterTypeEligibility?.quarterType || ""
  };

  const derivedQuarterTypeCode = React.useMemo(() => {
    if (apiData?.quarterTypeEligibility?.quarterTypeCode) return apiData.quarterTypeEligibility.quarterTypeCode;
    if (apiData?.quarterTypeEligibility?.quarter_type_code) return apiData.quarterTypeEligibility.quarter_type_code;
    if (apiData?.quarterTypeEligibility?.code) return apiData.quarterTypeEligibility.code;

    const typeName = (apiData?.quarterTypeEligibility?.quarterType || "")
      .replace(/\s*accommodation\s*/gi, "")
      .trim()
      .toLowerCase();
    if (!typeName || quarterTypes.length === 0) return "";

    const found = quarterTypes.find((t: any) =>
      String(t.name || "").toLowerCase().includes(typeName) ||
      String(t.code || "").toLowerCase() === typeName
    );
    return found ? found.code : "";
  }, [apiData?.quarterTypeEligibility, quarterTypes]);

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const [formData, setFormData] = useState({
    // Application details
    mobileNumber: "",
    email: "",
    poolId: "",
    eligibilityQuarter: "",
    spouseAllotted: "No",
    locationPref1: "",
    locationPref2: "",
    locationPref3: "",
    declarationAccepted: false,
    spouseDeclarationAccepted: false,
  });
  const [backendError, setBackendError] = useState<string | null>(null);

  const [errors, setErrors] = useState<any>({});
  const [isRefreshingLocations, setIsRefreshingLocations] = useState(false);
  const [showPoolInfo, setShowPoolInfo] = useState(false);

  const handleRefreshLocations = () => {
    if (formData.poolId && eligibility.id) {
      setFormData(prev => ({ ...prev, locationPref1: "", locationPref2: "", locationPref3: "" }));
      setRefreshTrigger(prev => prev + 1);
    } else {
      toast.warning("Please select a pool first to refresh locations");
    }
  };

  useEffect(() => {
    if (formData.poolId && eligibility.id) {
      setIsRefreshingLocations(true);
      dispatch(getPreferredLocations({
        poolTypeId: Number(formData.poolId),
        quarterTypeCode: derivedQuarterTypeCode || null,
      })).then((res: any) => {
        setIsRefreshingLocations(false);
        const data = res.payload?.data?.data || res.payload?.data || res.payload;
        if (Array.isArray(data)) {
          setLocations(data);
        } else {
          setLocations([]);
        }
      }).catch(() => {
        setIsRefreshingLocations(false);
        setLocations([]);
      });
    } else {
      setLocations(apiData?.locations || []);
    }
  }, [formData.poolId, eligibility.id, apiData?.locations, dispatch, refreshTrigger, derivedQuarterTypeCode]);

  useEffect(() => {
    dispatch(getEmployeeDetailsForStaffQuarter({}));
    dispatch(getStaffQuarterPools({}));
    dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
      if (res.payload?.success) {
        setQuarterTypes(res.payload.data || []);
      }
    });
    dispatch(getMyProfile({}));

    const timeout = setTimeout(() => setIsLoadingProfile(false), 8000);
    return () => clearTimeout(timeout);
  }, [dispatch]);

  useEffect(() => {
    const myProfileDetails = employeesState?.data?.data?.details;
    if (apiData || myProfileDetails) {
      setIsLoadingProfile(false);
      setFormData((prev) => ({
        ...prev,
        mobileNumber: myProfileDetails?.phone || employeeDetails?.mobileNumber || prev.mobileNumber,
        email: myProfileDetails?.email || employeeDetails?.email || prev.email,
        eligibilityQuarter: (eligibility.eligibilityForQuarterType || "").replace(/\s*accommodation\s*/gi, "") || prev.eligibilityQuarter,
      }));
    } else if (staffQuarterState?.loading === "fulfilled") {
      setIsLoadingProfile(false);
    }
  }, [apiData, staffQuarterState?.loading, employeesState?.data?.data?.details]);

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as any;

    // Prevent non-numeric or > 10 characters for mobile number
    if (name === "mobileNumber") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length > 10) return;
      setFormData((p) => ({ ...p, [name]: numericValue }));
    } else if (type === "checkbox") {
      setFormData((p) => ({ ...p, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((p) => {
        const updates: any = { [name]: value };
        if (name === "poolId") {
          updates.locationPref1 = "";
          updates.locationPref2 = "";
          updates.locationPref3 = "";
        }
        // If preference 1 is cleared, cascade clear 2 and 3
        if (name === "locationPref1" && !value) {
          updates.locationPref2 = "";
          updates.locationPref3 = "";
        }
        // If preference 2 is cleared, cascade clear 3
        if (name === "locationPref2" && !value) {
          updates.locationPref3 = "";
        }
        return { ...p, ...updates };
      });
    }
    // Clear error for the field when user types
    if (errors[name]) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    setBackendError(null);
  };

  const validateForm = () => {
    const newErrors: any = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Mobile Number validation (exactly 10 numeric digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.mobileNumber) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!phoneRegex.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Mobile number must be exactly 10 digits";
    }

    // Pool ID
    if (!formData.poolId) {
      newErrors.poolId = "Please select a pool";
    }

    // Location Preferences
    if (!formData.locationPref1) {
      newErrors.locations = "First location preference is mandatory";
    }

    // Declaration
    if (!formData.declarationAccepted) {
      newErrors.declarationAccepted = "You must accept the declaration";
    }

    // Spouse declaration
    if (formData.spouseAllotted === "Yes" && !formData.spouseDeclarationAccepted) {
      newErrors.spouseDeclarationAccepted = "You must accept the spouse declaration to proceed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("You must accept the declaration");
      return;
    }
    setBackendError(null);

    // Construct payload matching the backend requirements
    const payload: any = {
      empId: employeeDetails.employeeNumber || "",
      employeeName: employeeDetails.employeeName,
      employeeNumber: employeeDetails.employeeNumber,
      dateOfBirth: employeeDetails.dateOfBirth || "",
      designationId: employeeDetails.designationId || 0,
      payScale: employeeDetails.payscale,
      basicPay: employeeDetails.basicPay ? parseFloat(employeeDetails.basicPay) : 0,
      departmentId: employeeDetails.departmentId || null,
      dateOfJoining: employeeDetails.dateOfJoining || "",
      category: employeeDetails.category,
      employmentType: employeeDetails.natureOfEmployment,
      dateOfRetirement: employeeDetails.dateOfRetirement || "",
      employeeGroup: employeeDetails.employeeGroup || "",
      mobileNumber: formData.mobileNumber,
      email: formData.email,
      poolId: Number(formData.poolId),
      quarterTypeEligibilityId: eligibility.id,
      quarterTypeCode: derivedQuarterTypeCode || null,
      quarter_type_code: derivedQuarterTypeCode || null,
      isSpouseAllottedInDelhiNcr: formData.spouseAllotted === "Yes",
      isDeclarationAccepted: formData.declarationAccepted
    };

    if (formData.locationPref1) {
      payload.locationPreference1Id = Number(formData.locationPref1);
    }
    if (formData.locationPref2) {
      payload.locationPreference2Id = Number(formData.locationPref2);
    }
    if (formData.locationPref3) {
      payload.locationPreference3Id = Number(formData.locationPref3);
    }

    dispatch(submitStaffQuarterRegistration(payload)).then((res: any) => {
      if (res.payload?.status === 200 || res.payload?.success) {
        toast.success(res.payload.message || "Application submitted successfully");
        navigate(routes.staffQuarter.list);
      } else {
        // Map API errors if they exist
        if (res.payload?.errors) {
          setErrors(res.payload.errors);
        }
        const errorMsg = res.payload?.message || (typeof res.payload === 'string' ? res.payload : "Failed to submit application");
        setBackendError(errorMsg);
        toast.error(errorMsg);
      }
    });
  };



  const userJson = JSON.parse(localStorage.getItem("user") || "{}");
  const isHRAdmin = userJson?.role === "HR Admin" || userJson?.user_type === "HR Admin" || userJson?.role?.role_name === "HR Admin";
  let currentUserImage = employeesState?.data?.data?.details?.image ||
    employeesState?.data?.data?.image ||
    employeesState?.data?.data?.sap_employee_images?.image ||
    employeeDetails.image ||
    userJson?.sap_employee_images?.image;

  if (currentUserImage && !currentUserImage.startsWith("data:") && !currentUserImage.startsWith("http")) {
    currentUserImage = `data:image/jpeg;base64,${currentUserImage}`;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Application for Registration for DMRC Staff Quarters</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard || "#"}>Back</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  New Registration
                </li>
              </ol>
            </nav>
          </div>
        </div>
        {/* <div className="flex-grow-1 pb-4">
          <ApproverTracker
            approvers={[
              {
                id: employeeDetails.employeeNumber || "0",
                name: employeeDetails.employeeName || "User",
                status: "Pending",
                avatar: currentUserImage,
              },
              ...(employeeDetails.approverDetails || []).map((appr: any) => ({
                id: appr.empId,
                name: appr.name,
                status: "Pending",
                avatar: appr.image ? (appr.image.startsWith("data:") ? appr.image : `data:image/jpeg;base64,${appr.image}`) : undefined,
              })),
            ]}
          />
        </div> */}
        {backendError && (
          <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
            <i className="ti ti-alert-triangle me-2"></i>
            <strong>Error: </strong> {backendError}
            <button type="button" className="btn-close" onClick={() => setBackendError(null)} aria-label="Close"></button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="card mb-3">
            <div className="card-header">Employee Details (Read Only)</div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Employee Name</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.employeeName || ""} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Employee ID</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.employeeNumber || ""} />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Date of Birth</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={formatDate(employeeDetails.dateOfBirth)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Designation</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.designation || ""} />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Payscale</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.payscale || ""} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Basic Pay</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.basicPay || ""} />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Department</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.department || ""} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Date of Joining</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={formatDate(employeeDetails.dateOfJoining)} />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Category</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.category || ""} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Nature of Employment</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.natureOfEmployment || ""} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Date of Retirement</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={formatDate(employeeDetails.dateOfRetirement)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Employee Group</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={employeeDetails.employeeGroup || ""} />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">Application Details</div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Mobile Number <span style={{ color: "red" }}>*</span></label>
                  <input name="mobileNumber" placeholder="+91XXXXXXXXXX" value={formData.mobileNumber} onChange={handleChange} className={`form-control ${errors.mobileNumber ? 'is-invalid' : ''}`} />
                  {errors.mobileNumber && <div className="invalid-feedback">{errors.mobileNumber}</div>}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email ID <span style={{ color: "red" }}>*</span></label>
                  <input name="email" placeholder="abc@example.com" value={formData.email} onChange={handleChange} className={`form-control ${errors.email ? 'is-invalid' : ''}`} />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Name of Pool <span style={{ color: "red" }}>*</span></label>
                  <div className="d-flex align-items-center gap-2">
                    <select name="poolId" value={formData.poolId} onChange={handleChange} className={`form-select ${errors.poolId ? 'is-invalid' : ''}`}>
                      <option value="">Select pool</option>
                      {poolList.map((pool: any) => (
                        <option key={pool.id} value={pool.id}>
                          {pool.namePool || pool.name}
                        </option>
                      ))}
                    </select>
                    <div className="position-relative">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-icon btn-sm rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "38px", height: "38px", minWidth: "38px" }}
                        onMouseEnter={() => setShowPoolInfo(true)}
                        onMouseLeave={() => setShowPoolInfo(false)}
                        onClick={() => setShowPoolInfo(!showPoolInfo)}
                      >
                        <i className="ti ti-info-circle" style={{ fontSize: "20px" }}></i>
                      </button>

                      {/* Popover showing guidelines */}
                      {showPoolInfo && (
                        <div
                          className="position-absolute shadow-lg p-3 rounded"
                          style={{
                            top: "45px",
                            right: "0",
                            zIndex: 1050,
                            width: "360px",
                            backgroundColor: "#ffffff",
                            border: "1px solid #dee2e6",
                            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                          }}
                        >
                          <div className="fw-bold text-dark mb-2 pb-1 border-bottom" style={{ fontSize: "13px" }}>
                            Note: The Pool should be selected as per the following:
                          </div>
                          <div className="text-muted" style={{ fontSize: "11px", lineHeight: "1.5" }}>
                            <div className="mb-2"><strong className="text-dark">a) Operations:</strong> For employees belonging to Operations Cadre, irrespective of their current posting</div>
                            <div className="mb-2"><strong className="text-dark">b) Rolling Stock:</strong> For employees of O&M and Project posted in RS department</div>
                            <div className="mb-2"><strong className="text-dark">c) Electrical:</strong> For employees of O&M and Project posted in Electrical, E&M, Traction, Scada, Fire</div>
                            <div className="mb-2"><strong className="text-dark">d) S&T:</strong> For employees of O&M and Project posted in Signalling, AFC, Telecom, IT</div>
                            <div className="mb-2"><strong className="text-dark">e) Civil:</strong> For employees of O&M and Project posted in Civil, Works, P.way</div>
                            <div className="mb-0"><strong className="text-dark">f) General:</strong> For employees of HR, Finance, Stores, Legal, Unskilled, etc cadres</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {errors.poolId && <div className="invalid-feedback d-block">{errors.poolId}</div>}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Eligibility for Quarter Type</label>
                  <input className="form-control" disabled style={{ backgroundColor: "#f2f2f2", color: "#666", cursor: "not-allowed" }} value={formData.eligibilityQuarter} />
                </div>

                <div className="col-12 mb-2">
                  <label className="form-label d-block fw-semibold">Whether spouse is already availing a govt. accommodation in Delhi/NCR?: <span style={{ color: "red" }}>*</span></label>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="spouseAllotted" value="Yes" checked={formData.spouseAllotted === "Yes"} onChange={handleChange} />
                    <label className="form-check-label">Yes</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="spouseAllotted" value="No" checked={formData.spouseAllotted === "No"} onChange={handleChange} />
                    <label className="form-check-label">No</label>
                  </div>
                </div>

                {formData.spouseAllotted === "Yes" && (
                  <div className="col-12 mt-2 mb-3 bg-light p-3 rounded border border-warning">
                    <div className="form-check d-flex align-items-start gap-2">
                      <input
                        className="form-check-input mt-1"
                        type="checkbox"
                        id="spouseDeclaration"
                        name="spouseDeclarationAccepted"
                        checked={formData.spouseDeclarationAccepted}
                        onChange={handleChange}
                        style={{ minWidth: '16px' }}
                      />
                      <label className="form-check-label text-dark small" htmlFor="spouseDeclaration" style={{ cursor: 'pointer', lineHeight: '1.5' }}>
                        I declare that my spouse is already availing a government accommodation in Delhi/NCR. However, my spouse will surrender the accommodation allotted to him/her, before I occupy the DMRC staff quarter allotted to me. I understand, that under no circumstances, any delay will be entertained and I will be liable for suitable action in case of failure to do so.
                      </label>
                    </div>
                    {errors.spouseDeclarationAccepted && <div className="text-danger small mt-1 fw-bold ms-4">{errors.spouseDeclarationAccepted}</div>}
                  </div>
                )}

                <div className="col-12 mt-2 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">Location Preferences <span style={{ color: "red" }}>*</span></label>
                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={isRefreshingLocations || !formData.poolId} onClick={handleRefreshLocations}>
                      <i className={`ti ti-refresh me-1 ${isRefreshingLocations ? 'fa-spin' : ''}`} style={isRefreshingLocations ? { animation: "spin 1s linear infinite" } : {}}></i>
                      {isRefreshingLocations ? "Refreshing..." : "Refresh Locations"}
                    </button>
                    <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <select name="locationPref1" disabled={!formData.poolId} value={formData.locationPref1} onChange={handleChange} className={`form-select ${errors.locations ? 'is-invalid' : ''}`}>
                        <option value="">Select preference 1 *</option>
                        {locations
                          .filter((loc: any) => String(loc.id) !== formData.locationPref2 && String(loc.id) !== formData.locationPref3)
                          .map((loc: any) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-4 mb-2">
                      <select name="locationPref2" disabled={!formData.poolId || !formData.locationPref1} value={formData.locationPref2} onChange={handleChange} className="form-select">
                        <option value="">Select preference 2 (Optional)</option>
                        {locations
                          .filter((loc: any) => String(loc.id) !== formData.locationPref1 && String(loc.id) !== formData.locationPref3)
                          .map((loc: any) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-4 mb-2">
                      <select name="locationPref3" disabled={!formData.poolId || !formData.locationPref2} value={formData.locationPref3} onChange={handleChange} className="form-select">
                        <option value="">Select preference 3 (Optional)</option>
                        {locations
                          .filter((loc: any) => String(loc.id) !== formData.locationPref1 && String(loc.id) !== formData.locationPref2)
                          .map((loc: any) => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  {errors.locations && <div className="text-danger small mt-1 fw-bold">{errors.locations}</div>}
                </div>

                <div className="col-12 mt-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="declaration" name="declarationAccepted" checked={formData.declarationAccepted} onChange={handleChange} />
                    <label className="form-check-label">I hereby certify that the pool and location preferences mentioned above are correct and based on my understanding of DMRC's staff quarter policy.</label>
                    {errors.declarationAccepted && <div className="text-danger small mt-1">{errors.declarationAccepted}</div>}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Cancel</button>
              <button
                type="submit"
                className="btn btn-danger"
                disabled={formData.spouseAllotted === "Yes" && !formData.spouseDeclarationAccepted}
              >
                Submit Application
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewRegistration;
